import { useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase.js';
import { lastNDates } from '../../lib/dates.js';
import {
  morningBriefingStreak,
  checkoutStreak,
  devotionalStreak,
  workoutStreak,
  heatmap,
} from '../../lib/streaks.js';

export default function StreaksCard() {
  const [briefings, setBriefings] = useState([]);
  const [checkouts, setCheckouts] = useState([]);

  useEffect(() => {
    const since = lastNDates(35).slice(-1)[0]; // 35 days back to cover heatmap + a buffer

    Promise.all([
      supabase
        .from('daily_briefing')
        .select('date, devotional, workout, completed_at')
        .gte('date', since)
        .order('date', { ascending: false }),
      supabase
        .from('daily_checkout')
        .select('date, completed_at')
        .gte('date', since)
        .order('date', { ascending: false }),
    ]).then(([{ data: b }, { data: c }]) => {
      setBriefings(b ?? []);
      setCheckouts(c ?? []);
    });
  }, []);

  const stats = [
    {
      label: 'Briefing',
      count: morningBriefingStreak(briefings),
      heat: heatmap(briefings, (r) => !!r.completed_at, 30),
    },
    {
      label: 'Check-out',
      count: checkoutStreak(checkouts),
      heat: heatmap(checkouts, (r) => !!r.completed_at, 30),
    },
    {
      label: 'Devotional',
      count: devotionalStreak(briefings),
      heat: heatmap(briefings, (r) => r.devotional === true, 30),
    },
    {
      label: 'Workout',
      count: workoutStreak(briefings),
      heat: heatmap(briefings, (r) => r.workout === true, 30),
    },
  ];

  return (
    <div className="max-w-md mx-auto mt-4 bg-bg-card border border-border-base rounded p-5">
      <div className="text-xs uppercase tracking-wider text-text-secondary mb-3">
        Streaks
      </div>
      <div className="grid grid-cols-2 gap-3">
        {stats.map((s) => (
          <StreakStat key={s.label} {...s} />
        ))}
      </div>
    </div>
  );
}

function StreakStat({ label, count, heat }) {
  const milestone =
    count >= 100 ? 100 : count >= 30 ? 30 : count >= 10 ? 10 : 0;

  return (
    <div className={milestone > 0 ? 'animate-fade-in' : ''}>
      <div className="flex items-baseline gap-1.5">
        <span className="text-lg">🔥</span>
        <span className="text-xl font-bold text-text-primary">{count}</span>
        <span className="text-xs text-text-secondary">{label}</span>
      </div>
      <div className="mt-1.5 flex gap-[2px]">
        {/* Render oldest → newest so the right edge is today */}
        {[...heat].reverse().map((d, i) => (
          <span
            key={i}
            title={d.date}
            className={`h-1.5 flex-1 rounded-sm ${
              d.hit ? 'bg-accent-green' : 'bg-bg-input'
            }`}
          />
        ))}
      </div>
      {milestone > 0 && (
        <div className="text-[10px] text-accent-amber mt-1">
          ✨ {milestone}-day milestone
        </div>
      )}
    </div>
  );
}
